import { DownOutlined, EditOutlined, FilePdfOutlined } from '@ant-design/icons'
import type { Home, UserRole } from '@lmc/ts-common'
import {
  Button,
  Dropdown,
  FilterContainer,
  PageHeader,
  RangePicker,
  Spinner
} from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import ExternalLink from 'components/icons/ExternalLink'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePdfExport from 'hooks/usePdfExport'
import useResident from 'hooks/useResident'
import type jsPDF from 'jspdf'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { isMedicationRestrictedRole } from 'utils'
import { AdministrationRecordDrawer } from '../componentsV2/AdministrationRecordDrawer'
import { DiscontinuedRecordDrawer } from '../componentsV2/DiscontinuedRecordDrawer'
import AddStockModal from '../componentsV2/modalsV2/AddStockModal'
import AdministrationModal from '../componentsV2/modalsV2/AdministrationModal'
import DisposalModal from '../componentsV2/modalsV2/DisposalModal'
import PauseMedicationModal from '../componentsV2/modalsV2/PauseMedicationModal'
import StockCheckModal from '../componentsV2/modalsV2/StockCheckModal'
import UnpauseMedicationModal from '../componentsV2/modalsV2/UnpauseMedicationModal'
import PageWrapper from '../componentsV2/PageWrapper'
import { StockRecordDrawer } from '../componentsV2/StockRecordDrawer/StockRecordDrawer'
import Legend from '../EMARResidentScreenV2/Legend'
import MedicationsTable from '../EMARResidentScreenV2/MedicationsTable'
import { useCurrentStock } from '../hooks/useCurrentStock'
import { useMarChart } from '../hooks/useMarChart'
import { useMedicationSchedulePattern } from '../hooks/useMedicationSchedulePattern'
import { useStockRecords } from '../hooks/useStockRecords'
import {
  type MarChartCell,
  MedicationStockReason,
  type MedicationTableRow,
  type StockRecord
} from '../types'
import { transformMarChartData } from '../utils/administrationUtils'
import { buildStockRecord } from '../utils/buildStockRecord'
import { getMedicationIcon } from '../utils/medicationUtils'
import RecordsSection from './RecordsSection'

interface MedicationInformationScreenV2Props {
  testId?: string
}

// mm from the left edge of the A4 page, matching usePdfExport's own margin, and
// the vertical space the drawn header occupies before the captured content.
const PDF_HEADER_LEFT = 10
const PDF_HEADER_HEIGHT = 26

const MedicationInformationScreenV2 = ({
  testId
}: MedicationInformationScreenV2Props = {}) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { id: residentId, medication_id: medicationId } = useParams<{
    id: string
    medication_id: string
  }>()
  const navigate = useNavigate()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const user = useSelector(
    (state: {
      global: { user?: { medicationPermission?: boolean; role?: UserRole } }
    }) => state.global.user
  )
  // Role-based stand-in for the medication-management capability: audit-view,
  // family and group-level users cannot perform eMAR write actions. See
  // isMedicationRestrictedRole.
  const canManageMedication = !isMedicationRestrictedRole(user?.role)
  // Editing/discontinuing/pausing a medication and editing stock or
  // administration records additionally require the granular
  // medicationPermission flag, on top of the role check above.
  const canEditMedicationRecords =
    canManageMedication && Boolean(user?.medicationPermission)

  const { resident, isLoading: isLoadingResident } = useResident({
    id: residentId
  })

  const flags = useFeatureFlags({
    flagNames: ['emar_pause_unpause_medication']
  })?.data
  const pauseUnpauseEnabled =
    flags?.emar_pause_unpause_medication?.enabled ?? false

  const [startDate, setStartDate] = useState(
    dayjs().subtract(14, 'days').startOf('day')
  )
  const [endDate, setEndDate] = useState(dayjs().endOf('day'))

  const { data: medicationPattern, isLoading: isLoadingMedication } =
    useMedicationSchedulePattern({
      medicationId: medicationId || '',
      enabled: !!medicationId
    })

  const { records, isLoading: isLoadingStockRecords } = useStockRecords({
    medicationId: medicationId || '',
    startDate,
    endDate,
    includeChartData: false
  })

  const { currentStock, isLoading: isLoadingCurrentStock } = useCurrentStock({
    residentMedicationId: medicationId || '',
    residentId: residentId || '',
    enabled: !!medicationId && !!residentId
  })

  const { data: marChartData, isLoading: isLoadingMarChart } = useMarChart({
    residentId: residentId || '',
    startDate,
    endDate,
    enabled: !!residentId
  })

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [administrationDrawerOpen, setAdministrationDrawerOpen] =
    useState(false)
  const [discontinuedDrawerOpen, setDiscontinuedDrawerOpen] = useState(false)
  const [cellDrawerOpen, setCellDrawerOpen] = useState(false)
  const [cellDrawerRecord, setCellDrawerRecord] = useState<
    StockRecord | undefined
  >(undefined)

  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
  const [stockCheckModalOpen, setStockCheckModalOpen] = useState(false)
  const [addStockModalOpen, setAddStockModalOpen] = useState(false)
  const [disposalModalOpen, setDisposalModalOpen] = useState(false)
  const [administrationModalOpen, setAdministrationModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<StockRecord | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [pauseModalOpen, setPauseModalOpen] = useState(false)
  const [unpauseModalOpen, setUnpauseModalOpen] = useState(false)
  const pdfContentRef = useRef<HTMLDivElement>(null)

  const medicationType = useMemo(() => {
    if (!medicationPattern) {
      return 'scheduled'
    }
    return medicationPattern.prn ? 'prn' : 'scheduled'
  }, [medicationPattern])

  const isDiscontinued = useMemo(() => {
    if (!medicationPattern) {
      return false
    }
    return medicationPattern.status?.toLowerCase() === 'discontinued'
  }, [medicationPattern])

  const isActive = useMemo(() => {
    if (!medicationPattern) return false
    return medicationPattern.status?.toLowerCase() === 'active'
  }, [medicationPattern])

  const isPaused = useMemo(() => {
    if (!medicationPattern) return false
    return medicationPattern.status?.toLowerCase() === 'paused'
  }, [medicationPattern])

  const filteredMarChartData = useMemo(() => {
    if (!marChartData || !medicationId) {
      return null
    }
    return {
      ...marChartData,
      medications:
        marChartData.medications?.filter(
          (med: any) => med.residentMedicationId === medicationId
        ) || []
    }
  }, [marChartData, medicationId])

  const administrationTimingMap = useMemo(() => {
    const map = new Map<string, string>()
    filteredMarChartData?.medications?.forEach((med: any) => {
      med.roundTimings?.forEach((timing: any) => {
        if (!timing.timingTime) return
        timing.dateStatuses?.forEach((ds: any) => {
          if (ds.eventId) map.set(ds.eventId, timing.timingTime)
        })
      })
    })
    return map
  }, [filteredMarChartData])

  const tableData = useMemo(() => {
    return transformMarChartData(
      filteredMarChartData,
      medicationType,
      startDate,
      endDate
    )
  }, [filteredMarChartData, medicationType, startDate, endDate])

  const residentName = resident
    ? `${resident.name.first} ${resident.name.last}`
    : ''

  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0].startOf('day'))
      setEndDate(dates[1].endOf('day'))
    } else {
      setStartDate(dayjs().subtract(14, 'days').startOf('day'))
      setEndDate(dayjs().endOf('day'))
    }
  }

  const handleViewRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setDrawerOpen(true)
  }

  const handleViewAdministrationRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setAdministrationDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleCloseAdministrationDrawer = () => {
    setAdministrationDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleViewDiscontinuedRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setDiscontinuedDrawerOpen(true)
  }

  const handleCloseDiscontinuedDrawer = () => {
    setDiscontinuedDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleCellClick = (record: MedicationTableRow, dateKey: string) => {
    const cellData = record[dateKey] as MarChartCell | undefined
    if (!cellData?.outcomes || cellData.outcomes.length === 0) return

    const firstOutcome = cellData.outcomes[0]
    if (firstOutcome.action === 'MISSED' || firstOutcome.action === 'PAUSED') {
      return
    }

    const stockRecord = buildStockRecord({
      outcome: firstOutcome,
      row: record,
      dateKey
    })
    if (!stockRecord) return

    setCellDrawerRecord(stockRecord)
    setCellDrawerOpen(true)
  }

  const handleCloseCellDrawer = () => {
    setCellDrawerOpen(false)
    setCellDrawerRecord(undefined)
  }

  const handleEditFromDrawer = (
    record: StockRecord,
    actionType?: MedicationStockReason
  ) => {
    if (!canEditMedicationRecords) return

    if (record.type === 'administration') {
      setIsEditMode(true)
      setEditingRecord(record)
      setAdministrationModalOpen(true)
      return
    }

    if (!actionType) return

    setIsEditMode(true)
    setEditingRecord(record)

    switch (actionType) {
      case MedicationStockReason.STOCK_COUNT:
      case MedicationStockReason.RECONCILIATION:
      case MedicationStockReason.INITIAL_STOCK:
        setStockCheckModalOpen(true)
        break
      case MedicationStockReason.RESTOCK:
        setAddStockModalOpen(true)
        break
      case MedicationStockReason.DISPOSAL:
        setDisposalModalOpen(true)
        break
      default:
        break
    }
  }

  const handleStockCheckSuccess = () => {
    setStockCheckModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleStockCheckCancel = () => {
    setStockCheckModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleAddStockSuccess = () => {
    setAddStockModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleAddStockCancel = () => {
    setAddStockModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDisposalSuccess = () => {
    setDisposalModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDisposalCancel = () => {
    setDisposalModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleEditAdministrationCancel = () => {
    setAdministrationModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleEditAdministrationSuccess = () => {
    setAdministrationModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const expectedStock = currentStock?.currentStockLevel ?? 0

  const handleEditConfiguration = () => {
    navigate(`/residents/${residentId}/emar/${medicationId}/edit`)
  }

  const pdfTitle = `${residentName} - ${medicationPattern?.medicationName || ''} - ${startDate.format('DD-MM-YYYY')} to ${endDate.format('DD-MM-YYYY')}`

  // Drawn into the document rather than captured from the DOM: the page
  // header is a sticky bar of controls (Back, Read more, Download, Configure)
  // that has no place in a printed medication record — but the medication it
  // covers and the dates it spans do.
  const generatePdfHeader = (pdf: jsPDF) => {
    pdf.setFont('Helvetica')
    pdf.setFontSize(18)
    pdf.text(
      `${residentName} - ${medicationPattern?.medicationName || ''}`,
      PDF_HEADER_LEFT,
      14
    )
    pdf.setFontSize(12)
    pdf.text(
      `${startDate.format('DD-MM-YYYY')} - ${endDate.format('DD-MM-YYYY')}`,
      PDF_HEADER_LEFT,
      22
    )
    pdf.setDrawColor('#D9D9D9')
    pdf.setLineWidth(0.25)
    pdf.line(PDF_HEADER_LEFT, 26, 200, 26)
  }

  const handleDownloadPdf = usePdfExport(
    pdfContentRef as React.RefObject<HTMLInputElement>,
    pdfTitle,
    setIsDownloading,
    1,
    generatePdfHeader,
    PDF_HEADER_HEIGHT
  )

  if (
    isLoadingResident ||
    !resident ||
    isLoadingMedication ||
    isLoadingStockRecords ||
    isLoadingCurrentStock
  ) {
    return (
      <PageWrapper>
        <Spinner style={{ marginTop: '80px' }} />
      </PageWrapper>
    )
  }

  if (!medicationPattern) {
    return (
      <PageWrapper>
        <Text>{t('MEDICATION_NOT_FOUND')}</Text>
      </PageWrapper>
    )
  }

  return (
    <>
      <PageHeader
        onBack={() => navigate(-1)}
        title={
          <HeaderTitleContainer>
            {getMedicationIcon(medicationPattern?.status, 'large')}
            <MedicationTitleContainer>
              <HeadingStyled level={1}>
                {medicationPattern?.medicationName}
              </HeadingStyled>
              {isDiscontinued && (
                <InfoText>
                  {t('DISCONTINUED_SINCE', {
                    date: dayjs(
                      medicationPattern?.discontinued?.at ||
                        medicationPattern?.endDate
                    ).format('DD-MM-YYYY')
                  })}
                </InfoText>
              )}
              {isPaused && medicationPattern?.paused?.at && (
                <InfoText>
                  {t('PAUSED_SINCE', {
                    date: dayjs(medicationPattern.paused.at).format(
                      'DD-MM-YYYY'
                    )
                  })}
                </InfoText>
              )}
            </MedicationTitleContainer>
          </HeaderTitleContainer>
        }
        sticky
      >
        <HeaderButtonContainer>
          <ReadMoreLink
            href="https://support.logmycare.co.uk/en/collections/2749236-emar"
            target="_blank"
            rel="noreferrer"
          >
            {t('READ_MORE')} <ExternalLink />
          </ReadMoreLink>
          <Button
            onClick={handleDownloadPdf}
            variant="default"
            // The chart is the point of the export — capturing it mid-load would
            // put a spinner in the PDF where the MAR chart should be.
            disabled={isDownloading || isLoadingMarChart}
            isLoading={isDownloading}
            style={{ whiteSpace: 'nowrap' }}
          >
            <FilePdfOutlined />
            {t('DOWNLOAD_PDF')}
          </Button>
          {pauseUnpauseEnabled
            ? canEditMedicationRecords && (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'edit',
                        label: (
                          <MenuItemLabel>
                            <EditIconContainer>
                              <EditOutlined style={{ fontSize: 16 }} />
                            </EditIconContainer>
                            {t('EDIT_MEDICATION')}
                          </MenuItemLabel>
                        ),
                        onClick: handleEditConfiguration
                      },
                      ...(isActive && !isDiscontinued
                        ? [
                            {
                              key: 'pause',
                              label: (
                                <MenuItemLabel>
                                  {getMedicationIcon('paused')}
                                  {t('PAUSE_MEDICATION')}
                                </MenuItemLabel>
                              ),
                              onClick: () => setPauseModalOpen(true)
                            }
                          ]
                        : []),
                      ...(isPaused
                        ? [
                            {
                              key: 'unpause',
                              label: (
                                <MenuItemLabel>
                                  {getMedicationIcon('active')}
                                  {t('UNPAUSE_MEDICATION')}
                                </MenuItemLabel>
                              ),
                              onClick: () => setUnpauseModalOpen(true)
                            }
                          ]
                        : [])
                    ]
                  }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button variant="primary" style={{ whiteSpace: 'nowrap' }}>
                    {t('MANAGE_MEDICATION')} <DownOutlined />
                  </Button>
                </Dropdown>
              )
            : canManageMedication && (
                <Button
                  onClick={handleEditConfiguration}
                  variant="default"
                  style={{ whiteSpace: 'nowrap' }}
                  disabledByPermission={!user?.medicationPermission}
                  permissionDisabledText={t(
                    emarV2Constants.CONFIGURE_MEDICATION_PERMISSION_TOOLTIP
                  )}
                  withDisabledTooltipIcon={false}
                >
                  <EditOutlined />
                  {t('CONFIGURATION')}
                </Button>
              )}
        </HeaderButtonContainer>
      </PageHeader>
      <PageWrapper>
        {/* The export captures this column — filter, MAR chart and records —
            one direct child at a time, so each lands on the page as its own
            block rather than one tall screenshot sliced mid-record. */}
        <Container ref={pdfContentRef}>
          <FilterContainer title="">
            <RangePicker
              defaultValue={[startDate, endDate]}
              value={[startDate, endDate]}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
            />
          </FilterContainer>
          {isLoadingMarChart ? (
            <Spinner />
          ) : (
            <TableContainer>
              <TableHeader>
                <Legend testId={testId} />
              </TableHeader>
              <MedicationsTable
                startDate={startDate}
                endDate={endDate}
                type={medicationType}
                residentFirstName={resident?.name?.first || ''}
                onCellClick={handleCellClick}
                data={tableData}
              />
            </TableContainer>
          )}
          <RecordsSection
            records={records}
            medicationName={medicationPattern?.medicationName || ''}
            residentFirstName={resident?.name?.first || ''}
            residentFullName={
              resident ? `${resident.name.first} ${resident.name.last}` : ''
            }
            onViewRecord={handleViewRecord}
            onViewAdministrationRecord={handleViewAdministrationRecord}
            onViewDiscontinuedRecord={handleViewDiscontinuedRecord}
            administrationTimingMap={administrationTimingMap}
          />
        </Container>
        {stockCheckModalOpen && (
          <StockCheckModal
            open={stockCheckModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={
              medicationPattern?.stockQuantityType || currentStock?.unit || ''
            }
            onCancel={handleStockCheckCancel}
            onSuccess={handleStockCheckSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
            trackStock={medicationPattern?.trackStock}
          />
        )}
        {addStockModalOpen && (
          <AddStockModal
            open={addStockModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={
              medicationPattern?.stockQuantityType || currentStock?.unit || ''
            }
            onCancel={handleAddStockCancel}
            onSuccess={handleAddStockSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
          />
        )}
        {disposalModalOpen && (
          <DisposalModal
            open={disposalModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={
              medicationPattern?.stockQuantityType || currentStock?.unit || ''
            }
            onCancel={handleDisposalCancel}
            onSuccess={handleDisposalSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
          />
        )}
        {administrationModalOpen && (
          <AdministrationModal
            open={administrationModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            record={editingRecord || undefined}
            onCancel={handleEditAdministrationCancel}
            onSuccess={handleEditAdministrationSuccess}
          />
        )}
        <StockRecordDrawer
          trackStock={medicationPattern?.trackStock}
          isVisible={drawerOpen}
          onClose={handleCloseDrawer}
          record={
            selectedRecordId
              ? records.find((r) => r.id === selectedRecordId)
              : undefined
          }
          medicationName={medicationPattern?.medicationName || ''}
          controlledDrug={medicationPattern?.controlledDrug || false}
          onEdit={handleEditFromDrawer}
          canEdit={canEditMedicationRecords}
        />
        <AdministrationRecordDrawer
          isVisible={administrationDrawerOpen}
          onClose={handleCloseAdministrationDrawer}
          record={
            selectedRecordId
              ? records.find((r) => r.id === selectedRecordId)
              : undefined
          }
          medicationName={medicationPattern?.medicationName || ''}
          controlledDrug={medicationPattern?.controlledDrug || false}
          onEdit={handleEditFromDrawer}
          canEdit={canEditMedicationRecords}
        />
        <AdministrationRecordDrawer
          isVisible={cellDrawerOpen}
          onClose={handleCloseCellDrawer}
          record={cellDrawerRecord}
          medicationName={medicationPattern?.medicationName || ''}
          controlledDrug={medicationPattern?.controlledDrug || false}
          onEdit={handleEditFromDrawer}
          canEdit={canEditMedicationRecords}
        />
        <DiscontinuedRecordDrawer
          isVisible={discontinuedDrawerOpen}
          onClose={handleCloseDiscontinuedDrawer}
          record={
            selectedRecordId
              ? records.find((r) => r.id === selectedRecordId)
              : undefined
          }
          medicationName={medicationPattern?.medicationName || ''}
          controlledDrug={medicationPattern?.controlledDrug || false}
        />
        {medicationPattern && medicationId && (
          <>
            <PauseMedicationModal
              open={pauseModalOpen}
              medication={{
                id: medicationId,
                name: medicationPattern.medicationName
              }}
              residentName={residentName}
              onCancel={() => setPauseModalOpen(false)}
              onSuccess={() => setPauseModalOpen(false)}
            />
            <UnpauseMedicationModal
              open={unpauseModalOpen}
              medication={{
                id: medicationId,
                name: medicationPattern.medicationName
              }}
              residentName={residentName}
              onCancel={() => setUnpauseModalOpen(false)}
              onSuccess={() => setUnpauseModalOpen(false)}
            />
          </>
        )}
      </PageWrapper>
    </>
  )
}

const HeaderTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const HeaderButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`

const ReadMoreLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.color.category.clinicalCare};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

const TableContainer = styled.div`
  margin-top: 24px;
`

const TableHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`

const HeadingStyled = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  margin-right: 20px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const MedicationTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const InfoText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const MenuItemLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const EditIconContainer = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export default MedicationInformationScreenV2
